import { supabase } from '../lib/supabase';

/**
 * Creates the avatars storage bucket with proper configuration
 * Run this once to set up the storage bucket
 */
export const createAvatarBucket = async () => {
  console.log('=== Creating Avatar Storage Bucket ===');
  
  try {
    // Check if bucket already exists
    const { data: buckets, error: listError } = await supabase.storage.listBuckets();
    
    if (listError) {
      console.error('❌ Cannot list buckets:', listError);
      console.log('\n⚠️ You may need to create the bucket manually in Supabase dashboard');
      return { success: false, error: listError };
    }
    
    const bucketExists = buckets?.some(b => b.name === 'avatars');
    
    if (bucketExists) {
      console.log('✅ Avatars bucket already exists!');
      console.log('\n📋 Next steps:');
      console.log('1. Go to Supabase Dashboard > Storage > avatars');
      console.log('2. Click on "Policies" tab');
      console.log('3. Add the policies described in SUPABASE_STORAGE_SETUP.md');
      return { success: true, alreadyExists: true };
    }
    
    // Note: Creating buckets via client SDK is not typically allowed
    // This will usually fail due to permissions
    console.log('\n📋 Manual Setup Required:');
    console.log('The avatars bucket needs to be created manually.');
    console.log('\nFollow these steps:');
    console.log('1. Go to your Supabase Dashboard');
    console.log('2. Navigate to Storage in the left sidebar');
    console.log('3. Click "New bucket"');
    console.log('4. Use these settings:');
    console.log('   - Name: avatars');
    console.log('   - Public: ✅ Yes');
    console.log('   - File size limit: 5MB');
    console.log('   - Allowed MIME types:');
    console.log('     • image/jpeg');
    console.log('     • image/png');
    console.log('     • image/gif');
    console.log('     • image/webp');
    console.log('\n5. After creating the bucket, set up policies:');
    console.log('   See SUPABASE_STORAGE_SETUP.md for detailed policy setup');
    
    // Open the setup guide
    console.log('\n📖 Opening setup guide...');
    console.log('Check SUPABASE_STORAGE_SETUP.md for complete instructions');
    
    return { 
      success: false, 
      message: 'Manual setup required - see instructions above',
      setupGuide: 'SUPABASE_STORAGE_SETUP.md'
    };
    
  } catch (error) {
    console.error('❌ Error:', error);
    return { success: false, error };
  }
};

/**
 * Provides SQL commands for setting up storage policies
 */
export const getStoragePoliciesSQL = () => {
  const sql = `
-- ============================================
-- AVATAR STORAGE POLICIES FOR SUPABASE
-- ============================================
-- Run these commands in Supabase SQL Editor
-- after creating the 'avatars' bucket
-- ============================================

-- 1. Allow public read access to all avatars
CREATE POLICY "Public Avatar Read" 
ON storage.objects FOR SELECT 
USING (bucket_id = 'avatars');

-- 2. Allow authenticated users to upload their own avatar
CREATE POLICY "Users Can Upload Own Avatar" 
ON storage.objects FOR INSERT 
WITH CHECK (
  bucket_id = 'avatars' 
  AND auth.uid()::text = (string_to_array(name, '/'))[1]
);

-- 3. Allow users to update their own avatar
CREATE POLICY "Users Can Update Own Avatar" 
ON storage.objects FOR UPDATE 
USING (
  bucket_id = 'avatars' 
  AND auth.uid()::text = (string_to_array(name, '/'))[1]
);

-- 4. Allow users to delete their own avatar
CREATE POLICY "Users Can Delete Own Avatar" 
ON storage.objects FOR DELETE 
USING (
  bucket_id = 'avatars' 
  AND auth.uid()::text = (string_to_array(name, '/'))[1]
);

-- ============================================
-- To verify policies are working:
-- 1. Run: SELECT * FROM storage.objects WHERE bucket_id = 'avatars';
-- 2. Test upload from your application
-- ============================================
`;
  
  console.log(sql);
  return sql;
};

// Make available in browser console
if (typeof window !== 'undefined') {
  window.createAvatarBucket = createAvatarBucket;
  window.getStoragePoliciesSQL = getStoragePoliciesSQL;
  console.log('Avatar bucket setup available:');
  console.log('  - window.createAvatarBucket() - Check bucket setup');
  console.log('  - window.getStoragePoliciesSQL() - Get SQL for policies');
}